<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, reactive, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'brokers'),
    screen: computed(() => 'edit'),
  })
);

const agencyForm = reactive({
  creci: '',
  creci_uf: '',
  email_contato: '',
  nome_fantasia: '',
  site_url: '',
  telefone: '',
});

const asArray = value => (Array.isArray(value) ? value : []);
const pathValue = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => pathValue(value, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const valuesAt = (value, paths) =>
  paths.flatMap(path => asArray(pathValue(value, path)));
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const imageUrl = image => {
  if (typeof image === 'string') return image;
  if (!image || typeof image !== 'object') return '';

  return firstValue(image, ['url', 'file_url', 'image_url', 'avatar_url']);
};
const statusDotClass = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/ativo|public/.test(status)) return 'bg-emerald-500';
  if (/inativ|privad|suspens/.test(status)) return 'bg-[#9ea7b8]';
  return 'bg-violet-500';
};

const profile = computed(() => realData.data.value?.profile || {});
const source = computed(() => model.detail?.source || {});
const brokerAvailable = computed(() => Boolean(model.detail));
const brokerName = computed(() =>
  model.detail?.title ||
  firstValue(source.value, ['display_name', 'name', 'user.name'], 'Corretor não retornado')
);
const brokerRole = computed(() =>
  firstValue(source.value, ['role', 'user.role'], 'Função não retornada')
);
const brokerEmail = computed(() =>
  firstValue(source.value, ['email', 'user.email'], 'E-mail não retornado')
);
const brokerPhone = computed(() =>
  firstValue(source.value, ['phone', 'phone_number', 'user.phone'], 'Telefone não retornado')
);
const brokerAvatar = computed(() =>
  imageUrl(
    firstValue(source.value, [
      'avatar_url',
      'thumbnail',
      'user.avatar_url',
      'user.thumbnail',
    ])
  )
);
const brokerStatus = computed(() =>
  model.detail?.status ||
  firstValue(source.value, ['status', 'user.status'], 'Status não retornado')
);
const brokerCreci = computed(() =>
  firstValue(source.value, ['creci', 'creci_number', 'user.creci'], 'Não retornado')
);
const brokerManager = computed(() =>
  firstValue(
    source.value,
    ['manager.name', 'manager_name', 'gestor.name', 'gestor_responsavel.name'],
    'Não retornado pela API'
  )
);
const specialties = computed(() =>
  valuesAt(source.value, ['specialties', 'especialidades', 'skills'])
    .map(item =>
      typeof item === 'string'
        ? item
        : firstValue(item, ['name', 'label', 'title'])
    )
    .filter(Boolean)
);
const territories = computed(() =>
  valuesAt(profile.value, ['territories', 'territorios'])
    .map(item => firstValue(item, ['name', 'title', 'label']))
    .filter(Boolean)
    .slice(0, 5)
);
const branches = computed(() => asArray(profile.value.branches));
const brokerBranch = computed(() => {
  const brokerUserId = String(
    firstValue(source.value, ['user_id', 'user.user_id', 'id'])
  );
  return (
    branches.value.find(
      branch => String(branch.responsible_user_id || '') === brokerUserId
    ) || branches.value[0]
  );
});
const teamMembers = computed(() =>
  model.records
    .map(record => ({
      avatar: imageUrl(
        firstValue(record.source, [
          'avatar_url',
          'thumbnail',
          'user.avatar_url',
        ])
      ),
      id: record.id,
      initials: initials(record.title),
      name: record.title,
    }))
    .filter(member => member.id || member.name)
    .slice(0, 4)
);
const accessRows = computed(() => {
  const rows = [
    brokerRole.value && brokerRole.value !== 'Função não retornada'
      ? { label: 'Função na conta', value: brokerRole.value }
      : null,
    source.value.user?.administrator !== undefined
      ? {
          label: 'Administrador',
          value: source.value.user.administrator ? 'Sim' : 'Não',
        }
      : null,
  ].filter(Boolean);

  return rows;
});
const profileMetrics = computed(() => model.metrics.slice(0, 4));
const previewFacts = computed(() => model.detailFacts.slice(0, 3));
const completenessFields = computed(() => [
  brokerAvailable.value,
  brokerName.value !== 'Corretor não retornado',
  brokerRole.value !== 'Função não retornada',
  brokerEmail.value !== 'E-mail não retornado',
  Boolean(agencyForm.nome_fantasia),
  Boolean(agencyForm.creci),
  Boolean(agencyForm.email_contato),
  Boolean(agencyForm.telefone),
]);
const completeness = computed(() =>
  Math.round(
    (completenessFields.value.filter(Boolean).length /
      completenessFields.value.length) *
      100
  )
);
const completenessClass = computed(() => {
  if (completeness.value >= 100) return 'w-full';
  if (completeness.value >= 92) return 'w-11/12';
  if (completeness.value >= 84) return 'w-10/12';
  if (completeness.value >= 76) return 'w-9/12';
  if (completeness.value >= 68) return 'w-8/12';
  if (completeness.value >= 60) return 'w-7/12';
  if (completeness.value >= 52) return 'w-6/12';
  if (completeness.value >= 44) return 'w-5/12';
  if (completeness.value >= 36) return 'w-4/12';
  if (completeness.value >= 28) return 'w-3/12';
  if (completeness.value >= 20) return 'w-2/12';
  if (completeness.value > 0) return 'w-1/12';

  return 'w-0';
});
const canUpdateProfile = computed(() => {
  const permission =
    profile.value.can_update ?? profile.value.active_context?.can_update;

  return (
    typeof realData.actions?.brokers?.updateProfile === 'function' &&
    permission !== false
  );
});
const updateError = computed(() => error.value || model.localError || '');
const updateUnavailable = computed(() => {
  if (canUpdateProfile.value) return '';
  if (profile.value.can_update === false) {
    return 'Sua permissão atual não permite atualizar os dados da imobiliária.';
  }

  return 'A API atual não oferece atualização para os dados exibidos.';
});

watch(
  profile,
  nextProfile => {
    Object.assign(agencyForm, {
      creci: firstValue(nextProfile, ['creci']),
      creci_uf: firstValue(nextProfile, ['creci_uf']),
      email_contato: firstValue(nextProfile, ['email_contato', 'email']),
      nome_fantasia: firstValue(nextProfile, ['nome_fantasia', 'name']),
      site_url: firstValue(nextProfile, ['site_url']),
      telefone: firstValue(nextProfile, ['telefone', 'phone']),
    });
  },
  { immediate: true }
);

const saveAgencyProfile = async () => {
  if (!canUpdateProfile.value || saving.value) return;

  try {
    await realData.actions.brokers.updateProfile({ ...agencyForm });
  } catch (requestError) {
    if (!realData.error.value) throw requestError;
  }
};
</script>

<template>
  <main class="mx-auto w-full max-w-[1450px] px-5 pb-24 pt-3 text-[#101958]">
    <header class="grid grid-cols-[minmax(0,1fr)_345px_500px] items-start gap-4">
      <div class="pt-1">
        <p class="text-[10px] text-[#5a32bd]">
          Corretores <span class="mx-2 text-[#a5adc1]">/</span> {{ brokerName }}
          <span class="mx-2 text-[#a5adc1]">/</span> Editar
        </p>
        <div class="mt-1 flex items-center gap-4">
          <h1 class="text-[29px] font-bold tracking-[-0.04em]">Editar corretor</h1>
          <span class="rounded-lg bg-[#faf9fd] px-3 py-2 text-[8px] text-[#66739b]">
            {{ saving ? 'Salvando alterações' : canUpdateProfile ? 'Atualização pela API' : 'Atualização indisponível' }}
            <i class="ml-2 inline-block size-1.5 rounded-full" :class="saving ? 'bg-amber-500' : canUpdateProfile ? 'bg-emerald-500' : 'bg-[#a5adc1]'" />
          </span>
        </div>
      </div>

      <div class="mt-6 flex h-[58px] items-center gap-4 rounded-xl border border-[#e8e4ef] bg-white px-4 py-3">
        <span class="grid size-10 place-items-center rounded-xl bg-violet-50 text-[#5a24c7]"><span class="i-lucide-contact-round size-5" /></span>
        <span class="flex-1">
          <strong class="block text-[10px]">Perfil completo</strong>
          <span class="mt-2 block h-1.5 overflow-hidden rounded-full bg-[#e8e5ee]"><i class="block h-full rounded-full bg-gradient-to-r from-[#5922c8] via-[#7b5efe] to-[#30bce3]" :class="completenessClass" /></span>
        </span>
        <b class="text-[12px]">{{ completeness }}%</b>
        <span class="i-lucide-info size-3.5 text-[#66739b]" />
      </div>

      <article class="mt-6 flex h-[64px] items-center justify-between gap-3 rounded-xl border border-[#eee5ff] bg-gradient-to-r from-[#faf7ff] to-[#f8fcff] px-4">
        <span class="flex min-w-0 items-center gap-3">
          <span class="grid size-9 shrink-0 place-items-center rounded-xl bg-violet-100 text-[#5a24c7]"><span class="i-lucide-award size-5" /></span>
          <span class="min-w-0"><strong class="block text-[10px] text-[#4d1dc4]">Missão indisponível</strong><small class="mt-1 block truncate text-[8px] text-[#6b7696]">O backend não retornou missão, pontuação ou recompensa.</small></span>
        </span>
        <b class="shrink-0 rounded-lg bg-violet-100 px-3 py-2 text-[9px] text-[#6b46c1]">— pts</b>
      </article>
    </header>

    <p v-if="updateError" aria-live="polite" class="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[10px] text-rose-700">{{ updateError }}</p>
    <p v-else-if="updateUnavailable" class="mt-3 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[10px] text-[#6540a8]">{{ updateUnavailable }}</p>

    <section class="mt-4 grid grid-cols-[minmax(0,1fr)_500px] items-start gap-4">
      <article class="rounded-2xl border border-[#e8e4ef] bg-white p-5">
        <header class="flex items-center justify-between"><h2 class="text-[13px] font-bold">Dados pessoais e profissionais</h2><span class="rounded-full bg-[#f4f1f7] px-3 py-1 text-[8px] text-[#6e7893]">Leitura da API</span></header>

        <div class="mt-4 grid grid-cols-[180px_minmax(0,1fr)] gap-5">
          <div class="text-center">
            <div class="relative mx-auto w-fit rounded-full bg-gradient-to-br from-[#6f42dc] via-[#ff4ec8] to-[#25c5e8] p-1">
              <img v-if="brokerAvatar" :src="brokerAvatar" :alt="brokerName" class="size-[138px] rounded-full border-4 border-white object-cover" />
              <span v-else class="grid size-[138px] place-items-center rounded-full border-4 border-white bg-[#f2edff] text-[30px] font-bold text-[#5a24c7]">{{ initials(brokerName) }}</span>
              <span class="absolute bottom-1 right-0 grid size-9 place-items-center rounded-full border border-[#dcd4ea] bg-white text-[#9aa2b6]"><span class="i-lucide-camera-off size-4" /></span>
            </div>
            <small class="mt-3 block text-[8px] text-[#8590a8]">Imagem não é atualizável pela API.</small>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <label class="text-[9px] font-semibold">Nome de exibição <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]">{{ brokerName }}</span></label>
            <label class="text-[9px] font-semibold">Celular <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center gap-2 rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]"><span class="i-lucide-message-circle size-4 text-[#53608b]" />{{ brokerPhone }}</span></label>
            <label class="text-[9px] font-semibold">E-mail <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]">{{ brokerEmail }}</span></label>
            <label class="text-[9px] font-semibold">CRECI <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]">{{ brokerCreci }}</span></label>
          </div>
        </div>

        <section class="mt-3 grid grid-cols-[1fr_2fr] gap-3">
          <label class="text-[9px] font-semibold">Cargo <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center justify-between rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]">{{ brokerRole }}<span class="i-lucide-chevron-down size-3 text-[#9aa2b6]" /></span></label>
          <div class="text-[9px] font-semibold">Especialidades<span class="mt-2 flex min-h-10 items-center gap-2 rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal"><template v-if="specialties.length"><span v-for="item in specialties" :key="item" class="rounded-full bg-violet-100 px-3 py-1 text-[8px] font-medium text-[#5a24c7]">{{ item }}</span></template><small v-else class="text-[8px] text-[#8b94a8]">Não retornadas pela API</small><span class="i-lucide-chevron-down ml-auto size-3 shrink-0 text-[#9aa2b6]" /></span></div>
        </section>

        <section class="mt-3 grid grid-cols-2 gap-3">
          <div class="text-[9px] font-semibold">Loja <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center justify-between rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]"><span class="flex min-w-0 items-center gap-2 truncate"><span class="i-lucide-building-2 size-4 shrink-0" />{{ brokerBranch?.name || 'Não retornada pela API' }}</span><span class="i-lucide-chevron-down size-3 shrink-0 text-[#9aa2b6]" /></span></div>
          <div class="text-[9px] font-semibold">Gestor responsável <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center justify-between rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal text-[#5e6987]"><span class="flex min-w-0 items-center gap-2 truncate"><span class="i-lucide-user-round size-4 shrink-0" />{{ brokerManager }}</span><span class="i-lucide-chevron-down size-3 shrink-0 text-[#9aa2b6]" /></span></div>
          <div class="text-[9px] font-semibold">Equipe<span class="mt-2 flex h-10 items-center justify-between rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3"><span v-if="teamMembers.length" class="flex -space-x-2"><span v-for="member in teamMembers" :key="member.id" class="grid size-7 place-items-center overflow-hidden rounded-full border-2 border-white bg-[#f0eaff] text-[7px] font-bold text-[#5a24c7]"><img v-if="member.avatar" :src="member.avatar" :alt="member.name" class="h-full w-full object-cover" /><template v-else>{{ member.initials }}</template></span></span><small v-else class="text-[8px] text-[#8b94a8]">Não retornada pela API</small><span class="i-lucide-chevron-down size-3 shrink-0 text-[#9aa2b6]" /></span></div>
          <div class="text-[9px] font-semibold">Status <b class="text-rose-500">*</b><span class="mt-2 flex h-10 items-center justify-between rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 text-[10px] font-normal"><span class="flex items-center gap-2"><i class="size-2 rounded-full" :class="statusDotClass(brokerStatus)" />{{ brokerStatus }}</span><span class="i-lucide-chevron-down size-3 text-[#9aa2b6]" /></span></div>
        </section>

        <section class="mt-4"><h3 class="text-[10px] font-semibold">Biografia</h3><div class="mt-2 min-h-[78px] rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3 py-3 text-[9px] leading-5 text-[#77829d]">A API atual não retornou biografia nem oferece atualização deste campo.</div></section>
        <section class="mt-4"><h3 class="text-[10px] font-semibold">Regiões de atuação</h3><span class="mt-2 flex min-h-10 items-center gap-2 rounded-lg border border-[#ded9e9] bg-[#faf9fc] px-3"><template v-if="territories.length"><span v-for="territory in territories" :key="territory" class="rounded-full bg-violet-100 px-3 py-1 text-[8px] font-medium text-[#5a24c7]">{{ territory }}</span></template><small v-else class="text-[8px] text-[#8b94a8]">Não retornadas pela API</small><span class="i-lucide-chevron-down ml-auto size-3 shrink-0 text-[#9aa2b6]" /></span></section>

        <section class="mt-4 border-t border-[#eeeaf3] pt-4">
          <h3 class="text-[10px] font-semibold">Permissões e acessos</h3>
          <p class="mt-1 text-[8px] text-[#68749a]">Permissões disponíveis exclusivamente pelos dados retornados do corretor.</p>
          <div v-if="accessRows.length" class="mt-3 grid grid-cols-2 gap-3">
            <span v-for="access in accessRows" :key="access.label" class="flex min-h-[52px] items-center gap-3 rounded-xl border border-[#e8e4ef] bg-white px-3 py-2"><span class="grid size-8 place-items-center rounded-lg bg-violet-50 text-[#5a24c7]"><span class="i-lucide-shield-check size-4" /></span><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ access.label }}</strong><small class="mt-1 block truncate text-[7px] text-[#68749a]">{{ access.value }}</small></span><span class="i-lucide-square-check-big size-4 text-[#5a24c7]" /></span>
          </div>
          <p v-else class="mt-3 rounded-xl border border-[#e8e4ef] bg-[#faf9fc] px-3 py-3 text-[8px] text-[#7b85a0]">A API não retornou permissões editáveis para este corretor.</p>
        </section>

        <section class="mt-5 border-t border-[#eeeaf3] pt-4">
          <div class="flex items-center justify-between"><h3 class="text-[11px] font-bold">Dados editáveis da imobiliária</h3><small class="text-[8px] text-[#75809b]">{{ canUpdateProfile ? 'Atualização disponível' : 'Sem endpoint de atualização' }}</small></div>
          <div class="mt-4 grid grid-cols-2 gap-3">
            <label class="text-[9px] font-semibold">Nome fantasia<input v-model="agencyForm.nome_fantasia" :disabled="!canUpdateProfile" class="mt-2 h-10 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[10px] font-normal outline-none focus:border-[#7747df] disabled:cursor-not-allowed disabled:bg-[#faf9fc] disabled:text-[#8e97aa]" placeholder="Não retornado" /></label>
            <label class="text-[9px] font-semibold">Telefone<input v-model="agencyForm.telefone" :disabled="!canUpdateProfile" class="mt-2 h-10 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[10px] font-normal outline-none focus:border-[#7747df] disabled:cursor-not-allowed disabled:bg-[#faf9fc] disabled:text-[#8e97aa]" placeholder="Não retornado" /></label>
            <label class="text-[9px] font-semibold">E-mail de contato<input v-model="agencyForm.email_contato" :disabled="!canUpdateProfile" type="email" class="mt-2 h-10 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[10px] font-normal outline-none focus:border-[#7747df] disabled:cursor-not-allowed disabled:bg-[#faf9fc] disabled:text-[#8e97aa]" placeholder="Não retornado" /></label>
            <label class="text-[9px] font-semibold">Site<input v-model="agencyForm.site_url" :disabled="!canUpdateProfile" type="url" class="mt-2 h-10 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[10px] font-normal outline-none focus:border-[#7747df] disabled:cursor-not-allowed disabled:bg-[#faf9fc] disabled:text-[#8e97aa]" placeholder="Não retornado" /></label>
          </div>
          <div class="mt-3 grid grid-cols-[minmax(0,1fr)_130px] gap-3">
            <label class="text-[9px] font-semibold">CRECI da imobiliária<input v-model="agencyForm.creci" :disabled="!canUpdateProfile" class="mt-2 h-10 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[10px] font-normal outline-none focus:border-[#7747df] disabled:cursor-not-allowed disabled:bg-[#faf9fc] disabled:text-[#8e97aa]" placeholder="Não retornado" /></label>
            <label class="text-[9px] font-semibold">UF<input v-model="agencyForm.creci_uf" :disabled="!canUpdateProfile" class="mt-2 h-10 w-full rounded-lg border border-[#ded9e9] bg-white px-3 text-[10px] font-normal uppercase outline-none focus:border-[#7747df] disabled:cursor-not-allowed disabled:bg-[#faf9fc] disabled:text-[#8e97aa]" placeholder="UF" /></label>
          </div>
        </section>
      </article>

      <aside class="space-y-3">
        <article class="overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[12px] font-bold">Prévia do perfil público</h2>
          <div class="relative mt-3">
            <div class="grid h-[138px] place-items-center rounded-xl border border-dashed border-[#dcd4ea] bg-gradient-to-br from-[#e9e4f2] via-[#f6f3fb] to-[#e2f5fb] text-center"><span class="i-lucide-image-off size-5 text-[#9aa2b6]" /><small class="-mt-8 px-4 text-[8px] text-[#7d87a1]">Capa pública não retornada pela API</small></div>
            <div class="absolute -bottom-7 left-5 rounded-full bg-gradient-to-br from-[#6f42dc] via-[#ff4ec8] to-[#25c5e8] p-1"><img v-if="brokerAvatar" :src="brokerAvatar" :alt="brokerName" class="size-20 rounded-full border-4 border-white object-cover" /><span v-else class="grid size-20 place-items-center rounded-full border-4 border-white bg-[#f2edff] text-[18px] font-bold text-[#5a24c7]">{{ initials(brokerName) }}</span></div>
            <span class="absolute -bottom-1 left-[84px] size-5 rounded-full border-4 border-white" :class="statusDotClass(brokerStatus)" />
            <span class="absolute -bottom-2 right-3 rounded-full bg-white px-3 py-1 text-[7px] text-[#75809b] shadow-sm">Status conforme API</span>
          </div>

          <dl v-if="previewFacts.length" class="mt-9 grid grid-cols-3 text-center text-[8px]"><div v-for="fact in previewFacts" :key="fact.label" class="min-w-0"><dd class="truncate text-[10px] font-bold">{{ fact.value }}</dd><dt class="mt-1 truncate text-[#68749a]">{{ fact.label }}</dt></div></dl>
          <div v-else class="mt-9 grid grid-cols-3 text-center text-[8px] text-[#7e88a1]"><span><b class="block text-[10px] text-[#9aa2b6]">—</b>Não retornado</span><span><b class="block text-[10px] text-[#9aa2b6]">—</b>Não retornado</span><span><b class="block text-[10px] text-[#9aa2b6]">—</b>Não retornado</span></div>

          <h3 class="mt-3 text-[15px] font-bold">{{ brokerName }}</h3>
          <p class="mt-1 text-[9px] text-[#5e6a91]">{{ brokerRole }} · CRECI {{ brokerCreci }}</p>
          <div v-if="specialties.length" class="mt-3 flex flex-wrap gap-2"><span v-for="item in specialties" :key="item" class="rounded-full bg-violet-100 px-2 py-1 text-[7px] text-[#5a24c7]">{{ item }}</span></div>
          <p class="mt-3 text-[9px] leading-5 text-[#5e6a91]">Biografia e perfil social não foram retornados pela API.</p>
          <p class="mt-2 flex items-center gap-1 text-[8px] text-[#6e789d]"><span class="i-lucide-map-pin size-3" />{{ territories.length ? territories.join(', ') : 'Localização não retornada' }}</p>
          <button type="button" disabled class="mt-3 flex h-9 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-[#ded9e9] text-[9px] font-semibold text-[#9aa2b6]">Perfil público indisponível <span class="i-lucide-arrow-right size-3.5" /></button>
        </article>

        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Suas missões e pontos</h2><span class="text-[8px] text-[#8a94aa]">Sem contrato</span></header>
          <div class="mt-3 flex min-h-[74px] items-start gap-3 rounded-xl bg-[#faf9fd] p-3"><span class="grid size-8 shrink-0 place-items-center rounded-full bg-[#f1eef5] text-[#9ca5b6]"><span class="i-lucide-award size-4" /></span><span class="min-w-0 flex-1"><strong class="block text-[9px]">Missões não retornadas</strong><small class="mt-1 block text-[7px] leading-4 text-[#68749a]">A API não fornece objetivos, pontos ou recompensa para este corretor.</small></span><b class="text-[8px] text-[#9aa2b6]">— pts</b></div>
          <div class="mt-3 flex min-h-[74px] items-start gap-3 rounded-xl bg-emerald-50/60 p-3"><span class="grid size-8 shrink-0 place-items-center rounded-full bg-white text-emerald-400"><span class="i-lucide-circle-off size-4" /></span><span class="min-w-0 flex-1"><strong class="block text-[9px] text-[#667b74]">Conquista não retornada</strong><small class="mt-1 block text-[7px] leading-4 text-[#68749a]">Nenhuma conclusão pode ser exibida sem um contrato do backend.</small></span><b class="text-[8px] text-[#8ea59d]">— pts</b></div>
        </article>

        <article v-if="profileMetrics.length" class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between"><h2 class="text-[10px] font-bold">Indicadores da operação</h2><span class="i-lucide-chart-no-axes-combined size-4 text-[#5a24c7]" /></header>
          <div class="mt-4 grid grid-cols-2 gap-3"><div v-for="metric in profileMetrics" :key="metric.label" class="rounded-xl bg-[#faf9fc] px-3 py-3"><span :class="metric.icon" class="size-4 text-[#6433d0]" /><strong class="mt-2 block text-[14px]">{{ metric.value }}</strong><small class="mt-1 block text-[8px] text-[#73809c]">{{ metric.label }}</small></div></div>
        </article>
      </aside>
    </section>

    <footer class="fixed bottom-0 left-[156px] right-0 z-40 flex h-[72px] items-center justify-end gap-3 border-t border-[#eeeaf3] bg-white/95 px-8 shadow-[0_-8px_25px_rgba(38,25,86,0.04)] backdrop-blur">
      <button type="button" class="h-10 min-w-[140px] rounded-lg border border-[#ded9e9] bg-white px-5 text-[10px] font-semibold text-[#5a24c7]" :disabled="saving" @click="emit('navigate', 'detail')">Cancelar</button>
      <button type="button" disabled class="h-10 min-w-[160px] cursor-not-allowed rounded-lg border border-[#e6dff0] bg-white px-5 text-[10px] font-semibold text-[#a69db7]">Rascunho indisponível</button>
      <button type="button" class="flex h-10 min-w-[180px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#d31bd4] via-[#8d24e4] to-[#28a7e8] px-5 text-[10px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50" :disabled="saving || !canUpdateProfile" @click="saveAgencyProfile"><span class="i-lucide-save size-4" />{{ saving ? 'Salvando…' : 'Salvar alterações' }}</button>
    </footer>
  </main>
</template>
